import { useState, useCallback, useRef } from 'react'
import { Upload, X, FileSpreadsheet, AlertTriangle, Check, Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

type ParsedRow = {
  keyword: string
  volume: number
  cpc: number
  competition: number
  cluster: string
}

type ColumnMapping = {
  keyword: string | null
  volume: string | null
  cpc: string | null
  competition: string | null
  cluster: string | null
}

interface CsvImportModalProps {
  open: boolean
  onClose: () => void
  onImport: (file: File) => Promise<void>
  isImporting: boolean
}

const EXPECTED_COLUMNS: Record<keyof ColumnMapping, string[]> = {
  keyword: ['keyword', 'mot-clé', 'mot_cle', 'query', 'terme', 'search term'],
  volume: ['volume', 'avg. monthly searches', 'search volume', 'avg_searches', 'recherches', 'vol.'],
  cpc: ['cpc', 'cpc (eur)', 'cpc (usd)', 'cpc_high', 'coût par clic', 'cost per click'],
  competition: ['competition', 'competition_index', 'kd', 'difficulty', 'concurrence', 'comp.'],
  cluster: ['cluster', 'cluster_group', 'group', 'topic', 'catégorie', 'category', 'groupe'],
}

function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = { keyword: null, volume: null, cpc: null, competition: null, cluster: null }
  const lowerHeaders = headers.map((h) => h.trim().toLowerCase())

  for (const [field, aliases] of Object.entries(EXPECTED_COLUMNS)) {
    const idx = lowerHeaders.findIndex((h) => aliases.includes(h))
    if (idx !== -1) {
      mapping[field as keyof ColumnMapping] = headers[idx]
    }
  }
  return mapping
}

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length === 0) return { headers: [], rows: [] }

  // Detect separator
  const firstLine = lines[0]
  const sep = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ','

  const headers = lines[0].split(sep).map((h) => h.replace(/^["']|["']$/g, '').trim())
  const rows = lines.slice(1).map((line) =>
    line.split(sep).map((cell) => cell.replace(/^["']|["']$/g, '').trim())
  )
  return { headers, rows }
}

function safeInt(val: string): number {
  if (!val) return 0
  return parseInt(val.replace(/[,\s]/g, ''), 10) || 0
}

function safeFloat(val: string): number {
  if (!val) return 0
  return parseFloat(val.replace(/[,€$\s]/g, '').replace(',', '.')) || 0
}

const fmt = new Intl.NumberFormat('fr-FR')
const fmtCur = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

export function CsvImportModal({ open, onClose, onImport, isImporting }: CsvImportModalProps) {
  const [step, setStep] = useState<'upload' | 'preview'>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [rawRows, setRawRows] = useState<string[][]>([])
  const [mapping, setMapping] = useState<ColumnMapping>({ keyword: null, volume: null, cpc: null, competition: null, cluster: null })
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStep('upload')
    setFile(null)
    setHeaders([])
    setRawRows([])
    setMapping({ keyword: null, volume: null, cpc: null, competition: null, cluster: null })
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const processFile = useCallback(async (f: File) => {
    setError(null)
    setFile(f)

    try {
      const text = await f.text()
      const { headers: h, rows: r } = parseCSV(text)

      if (h.length === 0) {
        setError('Le fichier est vide ou ne contient pas d\'en-têtes.')
        return
      }
      if (r.length === 0) {
        setError('Le fichier ne contient aucune ligne de données.')
        return
      }

      setHeaders(h)
      setRawRows(r)

      const autoMap = autoMapColumns(h)
      if (!autoMap.keyword) {
        setError(`Colonne "keyword" introuvable. Colonnes détectées : ${h.join(', ')}`)
        return
      }
      setMapping(autoMap)
      setStep('preview')
    } catch {
      setError('Impossible de lire le fichier. Vérifiez l\'encodage (UTF-8 recommandé).')
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.tsv') || f.type === 'text/csv')) {
      processFile(f)
    } else {
      setError('Format non supporté. Utilisez un fichier .csv ou .tsv')
    }
  }, [processFile])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) processFile(f)
  }

  const handleImport = async () => {
    if (!file) return
    await onImport(file)
    handleClose()
  }

  // Build preview rows
  const previewRows: ParsedRow[] = rawRows.slice(0, 20).map((row) => {
    const get = (col: string | null) => {
      if (!col) return ''
      const idx = headers.indexOf(col)
      return idx !== -1 ? row[idx] ?? '' : ''
    }
    return {
      keyword: get(mapping.keyword),
      volume: safeInt(get(mapping.volume)),
      cpc: safeFloat(get(mapping.cpc)),
      competition: safeFloat(get(mapping.competition)),
      cluster: get(mapping.cluster),
    }
  }).filter((r) => r.keyword)

  const totalRows = rawRows.filter((row) => {
    if (!mapping.keyword) return false
    const idx = headers.indexOf(mapping.keyword)
    return idx !== -1 && row[idx]?.trim()
  }).length

  const hasVolume = previewRows.some((r) => r.volume > 0)
  const hasCpc = previewRows.some((r) => r.cpc > 0)
  const hasCluster = previewRows.some((r) => r.cluster)

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-50 bg-onyx/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className={cn(
          'bg-paper rounded-2xl shadow-elevated border border-ink-border w-full animate-fade-in',
          step === 'preview' ? 'max-w-4xl' : 'max-w-lg',
        )} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-ink-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal/10 flex items-center justify-center">
                <FileSpreadsheet size={18} className="text-teal" />
              </div>
              <div>
                <h2 className="text-lg font-serif font-bold text-ink">Importer des données CSV</h2>
                <p className="text-[11px] text-ink-faint">
                  {step === 'upload' ? 'SEMrush, Ubersuggest, Google Keyword Planner...' : `${totalRows} mots-clés détectés`}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 rounded-lg hover:bg-ink-border/30 transition-colors">
              <X size={18} className="text-ink-faint" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'upload' ? (
              /* ── STEP 1: Upload ── */
              <div>
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-ink-border/40 rounded-xl p-12 text-center cursor-pointer hover:border-teal/40 hover:bg-teal/5 transition-all"
                >
                  <Upload size={32} className="mx-auto text-ink-faint mb-3" />
                  <p className="text-sm font-medium text-ink mb-1">Glissez-déposez votre fichier CSV</p>
                  <p className="text-xs text-ink-faint">ou cliquez pour parcourir</p>
                  <p className="text-[10px] text-ink-faint/60 mt-3">Formats : .csv, .tsv · Encodage : UTF-8</p>
                </div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,text/csv" className="hidden" onChange={handleFileChange} />

                {error && (
                  <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-brick/10 border border-brick/20">
                    <AlertTriangle size={16} className="text-brick flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-brick">{error}</p>
                  </div>
                )}

                <div className="mt-6 bg-paper-warm rounded-lg p-4">
                  <p className="text-[11px] font-medium text-ink-faint mb-2">Format attendu :</p>
                  <code className="block text-[10px] text-teal bg-onyx/5 rounded px-3 py-2 font-mono leading-relaxed">
                    keyword,volume,cpc,competition,cluster<br />
                    plombier lorient,1200,2.50,0.65,plomberie<br />
                    electricien lorient,800,3.10,0.72,electricité
                  </code>
                </div>
              </div>
            ) : (
              /* ── STEP 2: Preview ── */
              <div>
                {/* Mapping summary */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {([
                    ['keyword', 'Mot-clé', true],
                    ['volume', 'Volume', false],
                    ['cpc', 'CPC', false],
                    ['competition', 'Compétition', false],
                    ['cluster', 'Cluster', false],
                  ] as const).map(([key, label, required]) => {
                    const mapped = mapping[key]
                    return (
                      <span key={key} className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border',
                        mapped ? 'bg-teal/10 text-teal border-teal/20' : required ? 'bg-brick/10 text-brick border-brick/20' : 'bg-ink-border/20 text-ink-faint border-ink-border/30'
                      )}>
                        {mapped ? <Check size={10} /> : '–'} {label}: {mapped || 'non détecté'}
                      </span>
                    )
                  })}
                </div>

                {/* Preview table */}
                <div className="border border-ink-border rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="bg-onyx text-paper">
                        <th className="text-left px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">#</th>
                        <th className="text-left px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Mot-clé</th>
                        {hasVolume && <th className="text-right px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Volume</th>}
                        {hasCpc && <th className="text-right px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">CPC</th>}
                        {mapping.competition && <th className="text-right px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Compétition</th>}
                        {hasCluster && <th className="text-left px-3 py-2.5 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Cluster</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} className={cn(i % 2 === 1 && 'bg-paper-warm/50')}>
                          <td className="px-3 py-2 text-[11px] text-ink-faint border-t border-ink-border/10">{i + 1}</td>
                          <td className="px-3 py-2 text-[12px] text-ink font-medium border-t border-ink-border/10">{row.keyword}</td>
                          {hasVolume && <td className="px-3 py-2 text-right text-[12px] text-ink border-t border-ink-border/10">{fmt.format(row.volume)}</td>}
                          {hasCpc && <td className="px-3 py-2 text-right text-[12px] text-ink border-t border-ink-border/10">{fmtCur.format(row.cpc)}</td>}
                          {mapping.competition && <td className="px-3 py-2 text-right text-[12px] text-ink border-t border-ink-border/10">{(row.competition * 100).toFixed(0)}%</td>}
                          {hasCluster && <td className="px-3 py-2 text-[12px] text-ink-faint border-t border-ink-border/10">{row.cluster || '—'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalRows > 20 && (
                  <p className="text-[10px] text-ink-faint text-center mt-2">
                    Aperçu des 20 premières lignes sur {totalRows} au total
                  </p>
                )}

                {/* Warnings */}
                {!hasVolume && (
                  <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-700">Aucune colonne "volume" détectée. Les mots-clés seront importés avec un volume à 0.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-ink-border bg-paper-warm/30 rounded-b-2xl">
            {step === 'preview' ? (
              <>
                <button onClick={reset} className="text-sm text-ink-faint hover:text-ink transition-colors">
                  ← Choisir un autre fichier
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-ink-faint">{file?.name}</span>
                  <button
                    onClick={handleImport}
                    disabled={isImporting || !mapping.keyword}
                    className="flex items-center gap-2 bg-teal text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-teal/90 transition-colors disabled:opacity-50"
                  >
                    {isImporting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {isImporting ? 'Import en cours...' : `Importer ${totalRows} mots-clés`}
                  </button>
                </div>
              </>
            ) : (
              <div className="ml-auto">
                <button onClick={handleClose} className="text-sm text-ink-faint hover:text-ink transition-colors">
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
